<script setup>
/**
 * 我的公会：顶栏「当前公会」能选的那些（名下 Agent 在册的、自己负责的；运营是全部）。
 * 每个公会列出我在里面的 Agent 和角色；「退出公会」= 名下在这个公会的 Agent 全部退出（公司 Agent 不能退，负责人要先换人）。
 */
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Badge, Button, ConfirmAction, EmptyState } from '@drama/ui'

import ListToolbar from '../components/ListToolbar.vue'
import { agentsIn, api, currentGuild, getToken, loadAccount, selectGuild, session } from '../api.js'
import { useListView } from '../list-view.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const message = ref('')
const error = ref('')

const rows = computed(() => currentGuild.options.map((guild) => {
  const mine = agentsIn(guild.slug).map((agent) => ({ id: agent.id, name: agent.full_name, role: agent.guilds.find((item) => item.slug === guild.slug).role }))
  return { ...guild, mine, leads: mine.some((agent) => agent.role === 'company') }
}))
const view = useListView(rows, {
  text: (guild) => guild.name,
  filters: [{ key: 'role', label: t('guilds.mine.allRoles'), options: [{ value: 'lead', label: t('guilds.mine.lead') }, { value: 'member', label: t('guilds.mine.memberOnly') }], match: (guild, value) => guild.leads === (value === 'lead') }],
  sorts: [{ value: 'name', label: t('guilds.sorts.name'), compare: (a, b) => a.name.localeCompare(b.name) }],
})
const isAdmin = computed(() => session.user !== null && session.user.role === 'admin')

/** @param {{ slug: string, name: string }} guild */
async function leave(guild) {
  message.value = ''
  error.value = ''
  try {
    await api('POST', `/api/voko/guilds/${guild.slug}/leave`)
    await loadAccount()
    message.value = t('guilds.mine.left', { guild: guild.name })
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

onMounted(async () => {
  if (getToken() === null) await router.push({ path: '/login', query: { next: route.fullPath } })
})
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.guilds') }}</p>
      <h1 class="page-head__title">{{ t('shell.myGuilds') }}</h1>
    </div>
    <div class="voko-actions">
      <Button variant="secondary" @click="router.push('/guilds')">{{ t('shell.discoverGuilds') }}</Button>
      <Button @click="router.push('/guilds/new')">{{ t('shell.newGuild') }}</Button>
    </div>
  </header>
  <p v-if="message" role="status">{{ message }}</p>
  <p v-if="error" role="alert">{{ error }}</p>

  <template v-if="currentGuild.loaded">
    <EmptyState v-if="rows.length === 0" :title="t('guilds.mine.empty')" :description="t('guilds.mine.emptyHint')">
      <template #action>
        <Button @click="router.push('/guilds')">{{ t('shell.discoverGuilds') }}</Button>
      </template>
    </EmptyState>
    <template v-else>
    <ListToolbar :view="view" />
    <p v-if="view.shown.value.length === 0" class="voko-muted">{{ t('list.noMatch') }}</p>
    <ul class="voko-my-guilds">
      <li v-for="guild in view.shown.value" :key="guild.slug" class="voko-panel">
        <h2>
          <router-link :to="`/guilds/${guild.slug}`">{{ guild.name }}</router-link>
          <Badge v-if="guild.slug === currentGuild.slug" variant="info" size="sm">{{ t('guilds.mine.current') }}</Badge>
          <Badge v-if="guild.leads" size="sm">{{ t('guilds.mine.lead') }}</Badge>
        </h2>
        <p v-if="guild.mine.length === 0" class="voko-muted">{{ t('guilds.mine.noAgents') }}</p>
        <p v-else class="voko-muted">{{ guild.mine.map((agent) => `${agent.name}（${t(`agents.roles.${agent.role}`)}）`).join('、') }}</p>
        <div class="voko-actions">
          <Button v-if="guild.slug !== currentGuild.slug" size="sm" variant="secondary" @click="selectGuild(guild.slug)">{{ t('guilds.mine.makeCurrent') }}</Button>
          <Button v-if="guild.leads || isAdmin" size="sm" variant="ghost" @click="router.push(`/guilds/${guild.slug}/settings`)">{{ t('shell.manage') }}</Button>
          <ConfirmAction v-if="guild.mine.some((agent) => agent.role === 'member')" :label="t('guilds.mine.leave')" :action="() => leave(guild)" :confirm-text="t('guilds.mine.leaveConfirm', { guild: guild.name })" :cancel-label="t('guilds.manageForm.cancel')" :ok-label="t('guilds.mine.leave')" :pending-label="t('guilds.manageForm.working')" danger />
        </div>
      </li>
    </ul>
    </template>
  </template>
</template>

<style scoped>
.voko-my-guilds {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
  gap: var(--drama-space-md);
  margin: 0;
  padding: 0;
  list-style: none;
}
.voko-my-guilds h2 {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
}
.voko-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm);
  margin-top: var(--drama-space-sm);
}
</style>
